/**
 * The Growth Score page: a full-page frame (`mx-auto max-w-[960px] px-6`) around the screens TP opened as artifacts in a
 * side panel (plan P4-1): the assessment, then a dimension, Search & AI, the brand or the cohort, each with a way back, and
 * no tabs. A "Back to chat" link goes to /onboarding. The hypotheses sit under the assessment (P4-4).
 *
 * It reads the BFF's assessment for the stored session: `not_ready` (the quiz is not stored) sends the person back to the chat.
 */
import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'

import { Button } from '@/components/ui/shadcn/button'
import { Card } from '@/components/ui/shadcn/card'
import { Spinner } from '@/components/ui/spinner'
import { onboardingKeys } from '@/features/onboarding-chat/hooks/queryKeys'
import { useSessionRecovery } from '@/features/onboarding-chat/hooks/useSessionRecovery'
import type { OnboardingSessionState } from '@/features/onboarding-chat/state/session'

import { ASSESSMENT_COPY } from './assessment-copy'
import { CHAT_PATH } from './paths'
import { GrowthNavProvider, HEADING_ATTR, OPENER_ATTR, openerKey, type GrowthNav, type GrowthScreen } from './GrowthPage'
import { GrowthScoreProvider, useGrowthScore, useGrowthScoreModel } from './GrowthScoreContext'
import { HypothesesCard } from './components/HypothesesCard'
import { AssessmentPage, BrandPage, CompetitorsPage, DimensionPage, SearchPage } from './pages'

function Screen({ screen }: { screen: GrowthScreen }) {
  const vm = useGrowthScore()
  switch (screen.kind) {
    case 'dimension':
      return <DimensionPage key={screen.dim} dim={screen.dim} />
    case 'search':
      return <SearchPage />
    case 'brand':
      return <BrandPage />
    case 'cohort':
      return <CompetitorsPage />
    case 'assessment':
      return (
        <div className="flex flex-col gap-8">
          <AssessmentPage />
          <HypothesesCard brand={vm.brand} hypotheses={vm.hypotheses} />
        </div>
      )
  }
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-dvh overflow-y-auto bg-background text-foreground">
      <main className="mx-auto flex max-w-[960px] flex-col gap-6 px-6 py-8">
        <h1 className="sr-only">{ASSESSMENT_COPY.pageLabel}</h1>
        <Link
          to={CHAT_PATH}
          className="-ml-2 inline-flex w-fit items-center gap-1.5 rounded-md px-2 py-1 text-tab text-ds-text-2 hover:bg-ds-ink-a05 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-focus"
        >
          <ArrowLeft aria-hidden className="size-4" />
          {ASSESSMENT_COPY.backToChat}
        </Link>
        {children}
      </main>
    </div>
  )
}

export function AssessmentScreen({
  sessionId,
  session,
}: {
  sessionId: string
  session: Pick<OnboardingSessionState, 'url' | 'answers' | 'brandProfile'>
}) {
  const client = useQueryClient()
  const recover = useSessionRecovery(sessionId, session.url)
  const model = useGrowthScoreModel({ sessionId, session, recover })
  const [screen, setScreen] = useState<GrowthScreen>({ kind: 'assessment' })
  const nav = useMemo<GrowthNav>(() => ({ open: setScreen }), [])
  const main = useRef<HTMLDivElement>(null)
  const shown = useRef(screen)

  // A new screen takes the focus: the assessment gets it back on the control that opened the drill-in, a drill-in on its own
  // heading. The first screen is left alone (the page just opened). Keyboard and screen reader users would otherwise stay on
  // a control that is gone.
  useEffect(() => {
    const left = shown.current
    if (left === screen) return
    shown.current = screen
    const root = main.current
    if (!root) return
    if (screen.kind === 'assessment') {
      const key = openerKey(left)
      const opener = key ? root.querySelector<HTMLElement>(`[${OPENER_ATTR}="${key}"]`) : null
      const heading = root.querySelector<HTMLElement>(`[${HEADING_ATTR}]`)
      ;(opener ?? heading)?.focus()
      return
    }
    // The page was just scrolled to its top: the heading is there, so keep it that way.
    root.querySelector<HTMLElement>(`[${HEADING_ATTR}]`)?.focus({ preventScroll: true })
  }, [screen])

  if (model.status === 'not_ready') return <Navigate to={CHAT_PATH} replace />

  if (model.status === 'loading') {
    return (
      <Frame>
        <div className="flex items-center gap-2 text-body-sm text-ds-text-2">
          <Spinner size="sm" label={ASSESSMENT_COPY.loading} />
          <span className="onb-shimmer">{ASSESSMENT_COPY.loading}</span>
        </div>
      </Frame>
    )
  }

  if (model.status === 'error') {
    return (
      <Frame>
        <Card role="alert" className="flex flex-col items-start gap-3 p-5">
          <h2 className="text-heading-sm text-foreground">{ASSESSMENT_COPY.failedTitle}</h2>
          <p className="text-body-sm text-ds-text-2">{ASSESSMENT_COPY.failedBody}</p>
          <Button type="button" onClick={() => void client.invalidateQueries({ queryKey: onboardingKeys.assessment(sessionId) })}>
            {ASSESSMENT_COPY.retry}
          </Button>
        </Card>
      </Frame>
    )
  }

  return (
    <Frame>
      <GrowthScoreProvider vm={model.vm}>
        <GrowthNavProvider value={nav}>
          <div ref={main}>
            <Screen screen={screen} />
          </div>
        </GrowthNavProvider>
      </GrowthScoreProvider>
    </Frame>
  )
}
